<template>
  <div class="media-image">
    <div
      v-if="!isCropperAreaVisible"
      class="media-image__background"
      :style="{ backgroundImage: `url(${props.src})` }"
    />
    <div v-if="!props.readonly" class="media-image__menu">
      <BaseButtonGroup variant="light">
        <BaseButtonIcon
          v-for="(item, index) in currentMenu"
          :key="index"
          :icon-name="item.icon"
          icon-color="var(--color-foreground-regular)"
          variant="light"
          @click="item.handler"
        />
        <BaseDivider
          v-if="!props.readonly"
          max-size="18px"
          vertical
        />
        <BaseButtonIcon
          v-if="!props.readonly"
          icon-name="vuesax:trash-linear"
          icon-color="var(--color-foreground-regular)"
          variant="light"
          @click="emit('delete')"
        />
      </BaseButtonGroup>
    </div>
    <BaseAspectRatio
      :width="props.aspectWidth"
      :height="props.aspectHeight"
    >
      <BaseCropperArea
        v-if="isCropperAreaVisible"
        ref="cropperRef"
        :model-value="props.src"
        :src="props.src"
        :file="originalFile || undefined"
        height="100%"
        :aspect-ratio="props.aspectWidth / props.aspectHeight"
      />
      <BaseImage
        v-else
        :src="props.src"
        :with-cross-origin="false"
        object-fit="contain"
        class="media-image__image"
        @click="handleOpenViewer"
      />
    </BaseAspectRatio>
  </div>
  <BaseImageViewer
    v-model:visible="isImageViewerVisible"
    v-model:selected-index="selectedIndex"
    :src-list="props.srcList || [props.src]"
  />
</template>

<script setup lang="ts">
import { getFileFromDataURL, isDataURL } from '~/shared/lib'
import {
  BaseAspectRatio,
  BaseButtonGroup,
  BaseButtonIcon,
  BaseCropperArea,
  BaseDivider,
  BaseImage,
  BaseImageViewer,
} from '~/shared/ui'

interface Props {
  src: string
  srcList?: string[]
  imageIndex?: number
  readonly?: boolean
  aspectWidth?: number
  aspectHeight?: number
}

const props = withDefaults(defineProps<Props>(), {
  aspectWidth: 1,
  aspectHeight: 1,
})

const emit = defineEmits<{
  (e: 'delete'): void
  (e: 'croppedFile', value: string): void
  (e: 'update:src', value: string): void
  (e: 'update:isCropping', value: boolean): void
}>()

const selectedIndex = defineModel<number>('selectedIndex', { default: 0 })

const originalFile = ref<File | undefined>()
const cropperRef = ref<ComponentPublicInstance<typeof BaseCropperArea>>()
const isImageViewerVisible = ref(false)
const isCropperAreaVisible = ref(false)

function handleOpenViewer() {
  if (props.imageIndex !== undefined) {
    selectedIndex.value = props.imageIndex
  }
  isImageViewerVisible.value = true
}

watch(isCropperAreaVisible, (value) => {
  emit('update:isCropping', value)
}, { immediate: true })

const currentMenu = computed(() => {
  if (!isCropperAreaVisible.value) {
    return [
      ...(props.readonly
        ? []
        : [{
            type: 'button',
            icon: 'vuesax:crop-linear',
            handler: () => {
              isCropperAreaVisible.value = true
            },
          }]),
    ]
  }

  return [
    {
      type: 'button',
      icon: 'vuesax:tick-circle-linear',
      handler: async () => {
        isCropperAreaVisible.value = false
        const file = await cropperRef.value?.cropImage()

        handleCroppedFile(file)
      },
    },
    {
      type: 'button',
      icon: 'vuesax:close-circle-linear',
      handler: () => {
        isCropperAreaVisible.value = false
      },
    },
  ]
})

async function handleCroppedFile(file: File) {
  emit('croppedFile', URL.createObjectURL(file))
  emit('update:src', URL.createObjectURL(file))
}

onMounted(() => {
  if (props.src && isDataURL(props.src)) {
    originalFile.value = getFileFromDataURL(props.src, 'image.png')
  }
})
</script>

<style lang="scss" scoped>
.media-image {
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: var(--radius-md-alt);
  overflow: hidden;

  &__background {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: blur(20px);
    transform: scale(1.1);
  }

  &__menu {
    position: absolute;
    top: rem(20px);
    right: rem(20px);
    z-index: 1;
  }

  &__image {
    cursor: pointer;
  }

  :deep(.base-cropper) {
    width: 100%;
    height: 100%;
  }
}
</style>
